import { useState, useEffect, useMemo, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Building,
  Database,
  Users,
  UserCircle,
  Briefcase,
  FileText,
  PieChart,
  LogOut,
  Menu,
  ChevronDown,
  ChevronRight,
  Circle,
  Receipt,
  History,
  X,
  Search,
  Settings,
  Bell,
  User,
  Zap,
  Command,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { MEDIA_URL } from "@/services/api";

type SidebarItemProps = {
  icon: React.ElementType;
  label: string;
  href: string;
  isActive?: boolean;
  hasSubmenu?: boolean;
  isSubmenuOpen?: boolean;
  onClick?: () => void;
  isSidebarCollapsed: boolean;
};

const SidebarItem = ({
  icon: Icon,
  label,
  href,
  isActive = false,
  hasSubmenu = false,
  isSubmenuOpen = false,
  onClick,
  isSidebarCollapsed,
}: SidebarItemProps) => {
  const content = (
    <div
      className={cn(
        "group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-300 ease-in-out",
        "hover:bg-gradient-to-r hover:from-white/10 hover:to-white/5 hover:shadow-lg hover:shadow-white/5",
        "active:scale-[0.98] active:transition-transform active:duration-75",
        isActive && "bg-gradient-to-r from-blue-500/20 to-purple-500/10 text-white shadow-lg shadow-blue-500/10 border border-white/10",
        !isActive && "text-slate-300 hover:text-white",
        hasSubmenu && "justify-between",
        isSidebarCollapsed && "justify-center"
      )}
    >
      <div className="flex items-center gap-3">
        <Icon className={cn(
          "h-4 w-4 flex-shrink-0 transition-all duration-300",
          isActive && "text-blue-400 drop-shadow-sm",
          "group-hover:scale-110 group-hover:text-white"
        )} />
        {!isSidebarCollapsed && (
          <span className="truncate font-medium tracking-wide">{label}</span>
        )}
      </div>
      {hasSubmenu && !isSidebarCollapsed && (
        <ChevronRight
          className={cn(
            "h-4 w-4 transition-all duration-300 ease-in-out",
            isSubmenuOpen && "rotate-90",
            "group-hover:text-white"
          )}
        />
      )}
    </div>
  );

  const wrappedContent = isSidebarCollapsed ? (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {onClick ? (
            <button onClick={onClick} className="w-full text-left">
              {content}
            </button>
          ) : (
            <Link to={href} className="block">
              {content}
            </Link>
          )}
        </TooltipTrigger>
        <TooltipContent side="right" className="bg-slate-900 text-white border-slate-700">
          <p>{label}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : (
    onClick ? (
      <button onClick={onClick} className="w-full text-left">
        {content}
      </button>
    ) : (
      <Link to={href} className="block">
        {content}
      </Link>
    )
  );

  return wrappedContent;
};

// Update the submenu type to be recursive
type SidebarSubmenuItem = {
  label: string;
  href: string;
  submenu?: SidebarSubmenuItem[];
};

// Update MenuSection to use the recursive type
type MenuSection = {
  label: string;
  items: {
    icon: React.ElementType;
    label: string;
    href: string;
    submenu?: SidebarSubmenuItem[];
  }[];
};

type Profile = {
  name: string;
  email: string;
  image: string;
};

// Update SidebarSubmenu to render recursively
const SidebarSubmenu = ({
  items,
  isOpen,
  activeHref,
  isSidebarCollapsed,
  openSubmenus,
  toggleAnySubmenu,
}: {
  items: SidebarSubmenuItem[];
  isOpen: boolean;
  activeHref: string;
  isSidebarCollapsed: boolean;
  openSubmenus: Record<string, boolean>;
  toggleAnySubmenu: (href: string) => void;
}) => {
  if (!isOpen || isSidebarCollapsed) return null;

  return (
    <div className="ml-6 mt-1 flex flex-col space-y-1">
      {items.map((item) => {
        const hasSubmenu = !!item.submenu?.length;
        // Only highlight as active if no submenu and matches href
        const isActive = !hasSubmenu && activeHref === item.href;
        const open = openSubmenus[item.href] || false;
        return (
          <div key={item.href}>
            <Link
              to={item.href === "#" ? "#" : item.href}
              className={cn(
                "flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm transition-all",
                isActive
                  ? "bg-primary/30 text-white"
                  : "text-sidebar-foreground/70 hover:bg-primary/20 hover:text-white"
              )}
              onClick={
                hasSubmenu
                  ? (e) => {
                      e.preventDefault();
                      toggleAnySubmenu(item.href);
                    }
                  : undefined
              }
            >
              <Circle size={8} />
              <span>{item.label}</span>
              {hasSubmenu && (
                <ChevronDown
                  size={14}
                  className={cn(
                    "ml-auto transition-transform",
                    open && "rotate-180"
                  )}
                />
              )}
            </Link>
            {hasSubmenu && (
              <SidebarSubmenu
                items={item.submenu}
                isOpen={open}
                activeHref={activeHref}
                isSidebarCollapsed={isSidebarCollapsed}
                openSubmenus={openSubmenus}
                toggleAnySubmenu={toggleAnySubmenu}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const isMobile = useIsMobile();

  // State for the sidebar
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(isMobile);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [profile, setProfile] = useState<Profile>({
    name: "",
    email: "",
    image: "",
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.authenticated.getProfile();
        setProfile(response.data);
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };
    fetchProfile();
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  }, [location.pathname, isMobile]);

  // Handle mobile menu toggle
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLogout = () => {
    setLogoutDialogOpen(true);
  };

  const confirmLogout = () => {
    logout();
    navigate("/login");
  };

  // Handler to toggle any submenu by href
  const toggleAnySubmenu = (href: string) => {
    setOpenSubmenus((prev) => ({ ...prev, [href]: !prev[href] }));
  };

  // Handle focus on first click and maintain cursor position
  const handleSearchClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.currentTarget.focus(); // Ensure focus on first click
  };

  // Handle input change and maintain focus
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    // Ensure focus is maintained after state update
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 0);
  };

  // Clear search query and focus
  const handleClearSearch = () => {
    setSearchQuery("");
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Define menu sections based on user role
  const menuSections: MenuSection[] =
    user?.role === "collector"
      ? [
          // Collector-specific menu sections
          {
            label: "Main",
            items: [
              {
                icon: LayoutDashboard,
                label: "Dashboard",
                href: "/dashboard",
              },
            ],
          },
          {
            label: "Core Modules",
            items: [
              {
                icon: FileText,
                label: "Collection",
                href: "/collection",
                submenu: [
                  {
                    label: "Record Payment",
                    href: "/collection/record-payment",
                  },
                  { label: "All Payments", href: "/collection/all-payments" },
                ],
              },
            ],
          },
        ]
      : [
          // Default menu sections for all other roles
          {
            label: "Main",
            items: [
              {
                icon: LayoutDashboard,
                label: "Dashboard",
                href: "/dashboard",
              },
            ],
          },
          {
            label: "Core Modules",
            items: [
              {
                icon: Building,
                label: "Organization",
                href: "/organization",
              },
              {
                icon: Database,
                label: "Masters",
                href: "/masters",
                submenu: [
                  { label: "Accounts", href: "/masters/accounts" },
                  { label: "Groups", href: "/masters/groups" },
                  { label: "Categories", href: "/masters/categories" },
                  { label: "Areas", href: "/masters/areas" },
                  { label: "Divisions", href: "/masters/divisions" },
                  { label: "Zones", href: "/masters/zones" },
                ],
              },
              {
                icon: Users,
                label: "Users",
                href: "/users",
              },
              {
                icon: UserCircle,
                label: "Consumers",
                href: "/consumers",
              },
              {
                icon: Briefcase,
                label: "Services",
                href: "/services",
              },
              {
                icon: FileText,
                label: "Collection",
                href: "/collection",
                submenu: [
                  {
                    label: "Record Payment",
                    href: "/collection/record-payment",
                  },
                  {
                    label: "Collection Configuration",
                    href: "/collection/configuration",
                  },
                  { label: "All Payments", href: "/collection/all-payments" },
                ],
              },
              {
                icon: Receipt,
                label: "Voucher",
                href: "/voucher",
                submenu: [
                  { label: "Payment", href: "/voucher/payment" },
                  { label: "Receipt", href: "/voucher/receipt" },
                ],
              },
            ],
          },
          {
            label: "Reports",
            items: [
              {
                icon: FileText,
                label: "Reports",
                href: "/reports",
                submenu: [
                  {
                    label: "Collection",
                    href: "#",
                    submenu: [
                      {
                        label: "Consumer",
                        href: "/reports/collection-consumer",
                      },
                      {
                        label: "Service",
                        href: "/reports/collection-service",
                      },
                      // Future collection-related reports can be added here
                    ],
                  },
                  {
                    label: "Recievable",
                    href: "/reports/recievable",
                  },
                  // Existing reports
                  {
                    label: "Account Statement",
                    href: "/reports/account-statement",
                  },
                  {
                    label: "Voucher Report",
                    href: "/reports/voucher-report",
                  },
                ],
              },
            ],
          },
          {
            label: "Logs",
            items: [
              {
                icon: History,
                label: "Activity Logs",
                href: "/logs",
              },
            ],
          },
        ];

  // Flatten menu items for search
  const flattenMenuItems = (sections: MenuSection[]) => {
    const items: Array<{ label: string; href: string; icon: React.ElementType; section: string }> = [];
    
    sections.forEach(section => {
      section.items.forEach(item => {
        items.push({ 
          label: item.label, 
          href: item.href, 
          icon: item.icon, 
          section: section.label 
        });
        
        // Add submenu items
        if (item.submenu) {
          const addSubmenuItems = (submenu: SidebarSubmenuItem[], parentLabel: string) => {
            submenu.forEach(subItem => {
              if (subItem.href !== "#") {
                items.push({ 
                  label: `${parentLabel} > ${subItem.label}`, 
                  href: subItem.href, 
                  icon: Circle, 
                  section: section.label 
                });
              }
              if (subItem.submenu) {
                addSubmenuItems(subItem.submenu, `${parentLabel} > ${subItem.label}`);
              }
            });
          };
          addSubmenuItems(item.submenu, item.label);
        }
      });
    });
    
    return items;
  };

  // Filter menu items based on search query
  const filteredMenuItems = useMemo(() => {
    if (!searchQuery.trim()) return [];
    
    const allItems = flattenMenuItems(menuSections);
    return allItems.filter(item => 
      item.label.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery, menuSections]);

  const toggleSidebar = () => {
    if (isMobile) {
      toggleMobileMenu();
    } else {
      setIsSidebarCollapsed(!isSidebarCollapsed);
    }
  };

  const toggleSubmenu = (href: string) => {
    setOpenSubmenu(openSubmenu === href ? null : href);
  };

  // Sidebar content component to avoid duplication
  const SidebarContent = () => (
    <>
      {/* Modern Header with Logo and Search */}
      <div className="border-b border-slate-700/50 bg-gradient-to-r from-slate-900/50 to-slate-800/30">
        <div
          className={cn(
            "flex h-16 items-center px-4",
            !isMobile && !isSidebarCollapsed && "justify-between",
            !isMobile && isSidebarCollapsed && "justify-center"
          )}
        >
          {/* Mobile close button */}
          {isMobile && isMobileMenuOpen && (
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMobileMenu}
              className="text-slate-400 hover:text-white hover:bg-slate-700/50 mr-3 transition-all duration-200"
            >
              <X className="h-5 w-5" />
            </Button>
          )}

          {/* Logo and Brand */}
          {((!isMobile && !isSidebarCollapsed) || (isMobile && isMobileMenuOpen)) && (
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg">
                <Zap className="h-4 w-4 text-white" />
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-white text-lg tracking-tight">ManterFlow</span>
                <span className="text-xs text-slate-400 font-medium">Collection Management</span>
              </div>
            </div>
          )}

          {/* Desktop collapse button */}
          {!isMobile && (
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              className="text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all duration-200"
            >
              <Menu className="h-5 w-5" />
            </Button>
          )}
        </div>

        {/* Search Bar - Only show when expanded */}
        {/* {!isSidebarCollapsed && (
          <div className="px-4 pb-4">
            <div className="relative">
              <Search
                className={cn(
                  "absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 transition-colors duration-200",
                  isSearchFocused ? "text-blue-400" : "text-slate-400"
                )}
              />
              <Input
                ref={searchInputRef}
                type="text"
                placeholder="Search menu items..."
                value={searchQuery}
                onChange={handleSearchChange}
                onClick={handleSearchClick}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                className={cn(
                  "pl-10 pr-10 py-2 bg-slate-800/50 border-slate-600/50 text-white placeholder:text-slate-400",
                  "focus:border-blue-400/50 focus:ring-1 focus:ring-blue-400/20 transition-all duration-200",
                  "hover:bg-slate-700/50"
                )}
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleClearSearch}
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 text-slate-400 hover:text-white"
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
        )} */}
      </div>

      <div className="flex-1 overflow-y-auto py-2 scrollbar-hide">
        {/* Search Results */}
        {searchQuery.trim() && (
          <div className="px-4 py-2">
            <div className="mb-3">
              <h3 className="text-xs font-semibold uppercase text-slate-400 tracking-wider">
                Search Results ({filteredMenuItems.length})
              </h3>
              {filteredMenuItems.length === 0 && (
                <p className="text-sm text-slate-500 mt-2">No items found for "{searchQuery}"</p>
              )}
            </div>
            <div className="space-y-1">
              {filteredMenuItems.map((item, index) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={`${item.href}-${index}`}
                    to={item.href}
                    onClick={() => setSearchQuery("")}
                    className={cn(
                      "group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-300 ease-in-out",
                      "hover:bg-gradient-to-r hover:from-white/10 hover:to-white/5 hover:shadow-lg hover:shadow-white/5",
                      isActive && "bg-gradient-to-r from-blue-500/20 to-purple-500/10 text-white shadow-lg shadow-blue-500/10 border border-white/10",
                      !isActive && "text-slate-300 hover:text-white"
                    )}
                  >
                    <item.icon className={cn(
                      "h-4 w-4 flex-shrink-0 transition-all duration-300",
                      isActive && "text-blue-400 drop-shadow-sm",
                      "group-hover:scale-110 group-hover:text-white"
                    )} />
                    <div className="flex flex-col">
                      <span className="truncate font-medium tracking-wide">{item.label}</span>
                      <span className="text-xs text-slate-500">{item.section}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Regular Menu - Only show when not searching */}
        {!searchQuery.trim() && (
          <div className="space-y-6">
            {menuSections.map((section) => (
              <div key={section.label} className="px-4">
                {!isSidebarCollapsed && (
                  <h3 className="mb-3 text-xs font-semibold uppercase text-slate-400 tracking-wider">
                    {section.label}
                  </h3>
                )}
                <div className="space-y-1">
                  {section.items.map((item) => {
                    const isActive =
                      location.pathname === item.href ||
                      location.pathname.startsWith(`${item.href}/`);
                    const hasSubmenu = !!item.submenu?.length;
                    const isSubmenuOpen = openSubmenu === item.href;

                    return (
                      <div key={item.href}>
                        <SidebarItem
                          icon={item.icon}
                          label={item.label}
                          href={hasSubmenu ? "#" : item.href}
                          isActive={isActive}
                          hasSubmenu={hasSubmenu}
                          isSubmenuOpen={isSubmenuOpen}
                          onClick={
                            hasSubmenu ? () => toggleSubmenu(item.href) : undefined
                          }
                          isSidebarCollapsed={isSidebarCollapsed}
                        />
                        {hasSubmenu && (
                          <SidebarSubmenu
                            items={item.submenu || []}
                            isOpen={isSubmenuOpen}
                            activeHref={location.pathname}
                            isSidebarCollapsed={isSidebarCollapsed}
                            openSubmenus={openSubmenus}
                            toggleAnySubmenu={toggleAnySubmenu}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modern User Profile Section */}
      <div className="mt-auto border-t border-slate-700/50 bg-gradient-to-r from-slate-900/30 to-slate-800/20">
        {!isSidebarCollapsed ? (
          <div className="p-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full justify-start p-3 h-auto hover:bg-slate-700/50 transition-all duration-200 group"
                >
                  <div className="flex items-center gap-3 w-full">
                    <Avatar className="h-8 w-8 ring-2 ring-slate-600 group-hover:ring-blue-400 transition-all duration-200">
                      <AvatarImage src={MEDIA_URL+profile?.image || ""} alt={profile?.name || "User"} />
                      <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-sm font-semibold">
                        {profile?.name?.charAt(0)?.toUpperCase() || "U"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col items-start flex-1 min-w-0">
                      <span className="text-sm font-semibold text-white truncate">
                        {profile?.name || "User"}
                      </span>
                      <span className="text-xs text-slate-400 truncate">
                        {profile?.email || "user@example.com"}
                      </span>
                    </div>
                    <ChevronDown className="h-4 w-4 text-slate-400 group-hover:text-white transition-colors duration-200" />
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                side="top"
                className="w-56 bg-slate-800 border-slate-700 shadow-xl"
              >
                {/* <DropdownMenuLabel className="text-slate-300">
                  My Account
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-slate-700" />
                <DropdownMenuItem className="text-slate-300 hover:bg-slate-700 hover:text-white focus:bg-slate-700 focus:text-white">
                  <User className="mr “‘2 h-4 w-4" />
                  Profile Settings
                </DropdownMenuItem> */}
                {/* <DropdownMenuItem className="text-slate-300 hover:bg-slate-700 hover:text-white focus:bg-slate-700 focus:text-white">
                  <Settings className="mr-2 h-4 w-4" />
                  Preferences
                </DropdownMenuItem>
                <DropdownMenuItem className="text-slate-300 hover:bg-slate-700 hover:text-white focus:bg-slate-700 focus:text-white">
                  <Bell className="mr-2 h-4 w-4" />
                  Notifications
                  <Badge variant="secondary" className="ml-auto bg-blue-500/20 text-blue-400 border-blue-500/30">
                    3
                  </Badge>
                </DropdownMenuItem> */}
                {/* <DropdownMenuSeparator className="bg-slate-700" /> */}
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="text-red-400 hover:bg-red-500/10 hover:text-red-300 focus:bg-red-500/10 focus:text-red-300"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : (
          <div className="p-3">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleLogout}
                    className="w-full h-10 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all duration-200"
                  >
                    <LogOut className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right" className="bg-slate-900 text-white border-slate-700">
                  <p>Sign Out</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
      </div>
    </>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className={cn(
          "hidden lg:flex flex-col bg-sidebar border-r border-white/10 transition-all duration-300 ease-in-out h-full overflow-hidden",
          isSidebarCollapsed ? "w-16" : "w-64"
        )}
      >
        <SidebarContent />
      </aside>

      {/* Mobile & Tablet Sidebar */}
      <div className="lg:hidden">
        {/* Mobile Menu Button (Hamburger) - only show when sidebar is CLOSED */}
        {!isMobileMenuOpen && (
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMobileMenu}
            className="fixed top-4 left-4 z-50 bg-sidebar text-white hover:bg-primary/80"
          >
            <Menu size={20} />
          </Button>
        )}

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50"
            onClick={toggleMobileMenu}
          />
        )}

        {/* Mobile Menu Content (the actual sliding sidebar) */}
        <aside
          className={cn(
            "fixed top-0 left-0 z-40 flex flex-col bg-sidebar h-full max-h-screen transition-transform duration-300 ease-in-out overflow-hidden",
            isMobileMenuOpen ? "translate-x-0" : "-translate-x-full",
            "w-64"
          )}
        >
          <SidebarContent />
        </aside>
      </div>

      <AlertDialog open={logoutDialogOpen} onOpenChange={setLogoutDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Are you sure you want to logout?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You will need to login again to access your account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmLogout}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Logout
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default Sidebar;
